import React, { useState, useEffect, useRef, Suspense, lazy, useCallback, startTransition } from 'react';
import ResponsivePagination from 'react-responsive-pagination';
import { toast } from "react-toastify";
import AxiosService from '../../../Utils/AxiosService';
import { Card, Spinner } from 'react-bootstrap';

const DirectionsForUseBase = lazy(() => import("./DirectionsForUse"));
// Memoized at module scope — stable reference prevents unnecessary re-renders
// when parent state (expandedIndexes, loader, etc.) changes.
const MemoizedDirectionsForUse = React.memo(DirectionsForUseBase);

const DirectionForUseParentElement = ({
  savedComments,
  commentsLoader,
  saveLoader,
  saveComment,
  loadComments,
  editFlag,
  labelId,
  handleShowAllComment,
  expandAll,
  handleExpandAll,
}) => {
  // Single stable AxiosService instance — avoids allocating a new object on
  // every render, which was happening with `const apiService = new AxiosService()`.
  const apiServiceRef = useRef(null);
  if (!apiServiceRef.current) apiServiceRef.current = new AxiosService();
  const apiService = apiServiceRef.current;

  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loader, setLoader] = useState(false);

  // Chunked progressive reveal — show first CHUNK_SIZE items immediately,
  // then schedule the next chunk during browser idle time. Because
  // MemoizedDirectionsForUse is memoized and onToggle is stable, each
  // incremental reveal only mounts the NEW items; existing items are skipped.
  const CHUNK_SIZE = 5;
  const [visibleCount, setVisibleCount] = useState(CHUNK_SIZE);
  const idleCallbackRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoader(true);
      try {
        const dataResponse = await apiService.getDirectionForUseData(labelId);
        setData(dataResponse.data ?? []);
      } catch (error) {
        const message = error?.response?.data?.message || "Something went wrong!";
        toast.error(message);
      } finally {
        setLoader(false);
      }
    };

    fetchData();
  }, [labelId]);

  const getDirectionForUseData = async (pageNumber) => {
    setLoader(true);
    try {
      const response = await apiService.getDirectionForUseData(labelId);
      setData(response.data ?? []);
    } catch (error) {
      const message = error?.response?.data?.message || "Something went wrong!";
      toast.error(message);
    } finally {
      setLoader(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    getDirectionForUseData(pageNumber);
  };

  const [expandedIndexes, setExpandedIndexes] = useState([]);

  // useCallback with a functional updater so this reference never changes.
  // Without this, every render creates a new function reference, which defeats
  // React.memo on MemoizedDirectionsForUse and forces all N items to re-render
  // whenever any single item is toggled.
  const onToggle = useCallback((index) => {
    setExpandedIndexes(prev => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
  }, []);

  // Reset visible window when fresh data arrives so the first chunk
  // appears immediately and the rest drip in during idle time.
  useEffect(() => {
    setVisibleCount(CHUNK_SIZE);
  }, [data]);

  // Progressively reveal the next chunk whenever the browser is idle.
  // requestIdleCallback ensures we never block user interaction; setTimeout(0)
  // is the fallback for browsers that don't support it.
  useEffect(() => {
    if (visibleCount >= (data?.length ?? 0)) return;

    const scheduleNextChunk = (cb) => {
      if (typeof requestIdleCallback !== 'undefined') {
        idleCallbackRef.current = requestIdleCallback(cb, { timeout: 300 });
      } else {
        idleCallbackRef.current = setTimeout(cb, 0);
      }
    };

    const cancelChunk = () => {
      if (typeof requestIdleCallback !== 'undefined') {
        cancelIdleCallback(idleCallbackRef.current);
      } else {
        clearTimeout(idleCallbackRef.current);
      }
    };

    scheduleNextChunk(() => {
      startTransition(() => {
        setVisibleCount(prev => Math.min(prev + CHUNK_SIZE, data.length));
      });
    });

    return cancelChunk;
  }, [visibleCount, data]);

  useEffect(() => {
    const len = data?.length ?? 0;
    setExpandedIndexes(new Array(len).fill(!!expandAll));
  }, [expandAll, data]);

  useEffect(() => {
    const labelMap = {};
    data.forEach((item) => {
      if (item.id && item.internalLabel?.["@none"]) {
        labelMap[item.id] = item.internalLabel["@none"];
      }
    });
    sessionStorage.setItem("labelMap", JSON.stringify(labelMap));
  }, [data]);

  const renderPagination = () => (
    <tr>
      <td colSpan={3}>
        <div className='p-2'>
          <ResponsivePagination
            total={Math.ceil(totalItems / 20)}
            current={currentPage}
            onPageChange={handlePageChange}
            maxWidth={70}
            previousLabel="Previous"
            nextLabel="Next"
          />
        </div>
      </td>
    </tr>
  );

  const renderLoading = () => (
    <tr>
      <td colSpan={3}>
        <Card className="mt-6">
          <Card.Body>
            <div className="text-center p-3">
              <Spinner animation="border" />
            </div>
          </Card.Body>
        </Card>
      </td>
    </tr>
  );

  const renderDirections = () => (
    <>
      {data.slice(0, visibleCount).map((item, index) => (
        <Suspense key={item.id ?? index} fallback={renderLoading()}>
          <MemoizedDirectionsForUse
            savedComments={savedComments}
            commentsLoader={commentsLoader}
            saveLoader={saveLoader}
            saveComment={saveComment}
            data={item}
            loadComments={loadComments}
            editFlag={editFlag}
            i={index}
            handleShowAllComment={handleShowAllComment}
            isExpanded={expandedIndexes[index]}
            onToggle={onToggle}
          />
        </Suspense>
      ))}
      {visibleCount < data.length && renderLoading()}
    </>
  );

  return (
    <>
      {/* {renderPagination()} */}
      {loader ? renderLoading() : renderDirections()}
    </>
  );
};

export default DirectionForUseParentElement;
